import { useEffect } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import useAdminData from "@/lib/useAdminData";
import { AdminHeader, StatCard, Card, StatusBadge, Empty, ErrorBanner, Loading } from "@/components/admin/ui";
import { formatINR } from "@/lib/format";
import { IndianRupee, ShoppingCart, Package, Users, TrendingUp, AlertTriangle } from "lucide-react";

export default function Dashboard() {
  // Last successfully loaded totals are kept if a refresh fails: a failed request
  // must never turn real numbers into zeros.
  const { data: d, loading, error, stale, reload } = useAdminData(
    () => api.get("/admin/dashboard").then((r) => r.data)
  );

  useEffect(() => { reload(); }, [reload]);

  if (!d && loading) return <Loading text="Loading dashboard…" />;
  if (!d) {
    return (
      <div>
        <AdminHeader title="Dashboard" subtitle="Overview of your store performance" />
        <ErrorBanner
          message={error?.message || "The dashboard could not be loaded."}
          onRetry={reload}
        />
      </div>
    );
  }

  return (
    <div>
      <AdminHeader title="Dashboard" subtitle="Overview of your store performance" />
      {error && <ErrorBanner className="mb-4" message={error.message} stale={stale} onRetry={reload} />}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-6">
        <StatCard label="Total Sales" value={formatINR(d.totalSales)} icon={IndianRupee} tone="emerald" />
        <StatCard label="Today's Sales" value={formatINR(d.todaySales)} icon={TrendingUp} tone="amber" />
        <StatCard label="Orders" value={d.orderCount} icon={ShoppingCart} tone="slate" />
        <StatCard label="Pending Orders" value={d.pendingOrders} icon={ShoppingCart} tone="amber" />
        <StatCard label="Products" value={d.productCount} icon={Package} tone="slate" />
        <StatCard label="Customers" value={d.customerCount} icon={Users} tone="slate" />
        <StatCard label="Low Stock" value={d.lowStockCount} icon={AlertTriangle} tone="red" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-900">Recent Orders</h3>
            <Link to="/admin/orders" className="text-sm text-amber-600 font-medium">View all</Link>
          </div>
          {d.recentOrders?.length === 0 ? <Empty text="No orders yet" /> : (
            <div className="space-y-2">
              {(d.recentOrders || []).map((o) => (
                <Link key={o.id} to={`/admin/orders/${o.id}`} className="flex items-center justify-between p-2.5 rounded-lg hover:bg-slate-50">
                  <div><p className="font-mono text-sm font-semibold">{o.orderNumber}</p><p className="text-xs text-slate-500">{new Date(o.createdAt).toLocaleDateString("en-IN")}</p></div>
                  <div className="text-right"><p className="font-mono text-sm">{formatINR(o.total)}</p><StatusBadge status={o.status} /></div>
                </Link>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-5">
          <h3 className="font-semibold text-slate-900 mb-4">Low Stock Alerts</h3>
          {d.lowStock?.length === 0 ? <Empty text="All products well stocked" /> : (
            <div className="space-y-2">
              {(d.lowStock || []).map((l, i) => (
                <div key={i} className="flex items-center justify-between p-2.5 rounded-lg bg-red-50/50">
                  <div><p className="text-sm font-medium text-slate-900">{l.product}</p><p className="text-xs text-slate-500 font-mono">{l.sku}</p></div>
                  <span className="text-sm font-mono font-semibold text-red-600">{l.available} left</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <Card className="p-5 mt-6">
        <h3 className="font-semibold text-slate-900 mb-4">Order Status Overview</h3>
        <div className="flex flex-wrap gap-3">
          {Object.entries(d.statusBreakdown || {}).map(([k, v]) => (
            <div key={k} className="px-4 py-3 rounded-lg bg-slate-50 border border-slate-200">
              <p className="text-lg font-bold font-mono text-slate-900">{v}</p>
              <p className="text-xs text-slate-500">{k.replace(/_/g, " ")}</p>
            </div>
          ))}
          {Object.keys(d.statusBreakdown || {}).length === 0 && <Empty text="No orders yet" />}
        </div>
      </Card>
    </div>
  );
}
